import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';
// utils
import { setSession } from '../utils/jwt';

import conObj from '../utils/connection-assistant';
import { multiRequest } from '../redux/slices/lookup';
import { getFingerprint, getLocalId } from '../utils/formatObject';
import { closeLoadingScreen, openLoadingScreen } from '../redux/slices/settings';
import { dispatch as globalDispatch } from '../redux/store';
import { openModal } from '../redux/slices/authUiSlice';

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user, userProfile } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      userProfile,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  getAccountProfile: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');

        const queryString = window.location.search;

        const urlParams = new URLSearchParams(queryString);
        const referralCode = urlParams.get('aff');

        if (referralCode) {
          localStorage.setItem('referralCode', referralCode);

          globalDispatch(
            openModal({
              view: 'register',
            })
          );
        }

        if (window.location.pathname.indexOf('auth/reset-password') !== -1) {
          const token = window.location.pathname.match(/[^]+$/)[0];
          localStorage.setItem('resetToken', token);
        }

        globalDispatch(openLoadingScreen());
        const response = await multiRequest();
        
        globalDispatch(closeLoadingScreen());
        if (accessToken) {
          setSession(accessToken);

          // const response = await getUserMemberBalance();

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user: {},
              userProfile: response?.data?.data,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
              userProfile: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
            userProfile: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (
    data,
    loginData = { token: null, firstName: '', username: '', fullName: '', lastName: '', email: '' }
  ) => {
    localStorage.removeItem('accessToken');

    let errMsg = '';

    let { token } = loginData;
    let { firstName, username, fullName, lastName, email } = loginData;

    if (!token) {
      const { fpResult, visitorId } = await getFingerprint();
      const localId = await getLocalId();
      const response = await conObj.post('login.json', {
        ...data,
        fingerprint: visitorId,
        fingerprint_data: fpResult,
        local_id: localId,
      });
      const responseData = response.data?.data?.data;
      errMsg = response.data?.data?.msg;
      token = response.data?.data?.token;

      firstName = responseData?.first_name;
      username = responseData?.username;
      fullName = responseData?.full_name;
      lastName = responseData?.last_name;
      email = responseData?.email;
    }

    if (token) {
      setSession(token);

      window.location.reload();

      dispatch({
        type: 'LOGIN',
        payload: {
          user: { displayName: firstName, username, fullName, lastName, email },
        },
      });
    } else throw new Error(errMsg);
  };

  const register = async (data) => {
    localStorage.removeItem('accessToken');

    const { fpResult, visitorId } = await getFingerprint();
    const localId = await getLocalId();
    const response = await conObj.post('register.json', {
      ...data,
      fingerprint: visitorId,
      fingerprint_data: fpResult,
      local_id: localId,
    });

    const {
      token,
      data: { first_name: firstName, username, full_name: fullName, last_name: lastName, email },
    } = response.data.data;

    if (token) {
      setSession(token);

      window.location.reload();

      dispatch({
        type: 'LOGIN',
        payload: {
          user: { displayName: firstName, username, fullName, lastName, email },
        },
      });
    } else throw new Error(response.data?.data?.msg);
  };

  const logout = async () => {
    try {
      await conObj.post('member_logout.json');
    } catch (error) {
      console.error(error);
    }
    setSession(null);
    dispatch({ type: 'LOGOUT' });
    window.location.reload();
  };

  const getAccountProfile = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        const response = await conObj.post('profile.json');

        const responseData = response.data?.data?.data;

        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user: {},
            userProfile: responseData,
          },
        });
      }
    } catch (err) {
      console.error(err);
      dispatch({
        type: 'INITIALIZE',
        payload: {
          isAuthenticated: false,
          user: null,
          userProfile: null,
        },
      });
    }
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        getAccountProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
